// ═══════════════════════════════════════════════════════════════════════════════════════════
// GroupRow — one row of the tree, at EITHER level. A sub-group is a category as far as
// capabilities go (14-W3 §1): handle · ↑↓ · inline rename · visibility toggle · item count ·
// history · delete. The only asymmetry is deliberate:
//
//   · "＋ תת-קבוצה" appears on a CATEGORY row ONLY. The three-level cap is communicated by the
//     ABSENCE of the control on a sub-group row — not a disabled button, not an explanatory
//     tooltip (§1, owner/UX decision). The positive sentence above the tree carries the rule.
//   · "+ הוספה ל…" appears on a SUB-GROUP row only, because items only ever live in sub-groups.
//
// ⚠️ The delete affordance has TWO shapes and the parent chooses (§4): `deleteMode="inline"` for
// an empty group (two-step confirm right in the row, with a timeout back to normal, exactly like
// the mock) and `deleteMode="dialog"` when there are children or items to account for. The
// mock's disabled-delete-with-a-tooltip is gone.
//
// Search active ⇒ the handle and the nudge buttons are NOT RENDERED AT ALL (§3.8). Sending a
// FILTERED list to SP3/SP6 would leave the hidden rows holding stale SortOrder values that
// collide with the new ones — silent corruption that only shows up on the portal.
//
// CATEGORY STYLE (2026-10-06, _contract/CONTRACT.md §4) — a CATEGORY row only: the palette button
// (opens CategoryStyleDialog through `onEditStyle`) and, beside the title, a swatch of what the
// portal will paint — icon on its colour, a bare colour dot, or nothing at all.
// ═══════════════════════════════════════════════════════════════════════════════════════════

import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, IconButton, TextField, Tooltip, Typography } from '@material-ui/core';
import {
    Edit, Visibility, VisibilityOff, DeleteOutline, History, ExpandMore, ChevronLeft, ChevronRight,
    PaletteOutlined
} from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { CC, GroupDto } from '../../../Models/ClalCenter/ClalCenter';
import { CATEGORY_DEFAULT_BG, safeIconSrc } from './CategoryStyleDialog';
import { HandleProps, useHandleStyles } from './SortableRow';

const INLINE_CONFIRM_MS = 4000;

interface Props {
    group: GroupDto;
    level: 1 | 2;
    position: number;
    total: number;
    itemCount: number;
    liveCount: number;
    draftCount: number;
    /** Level 1 only. */
    isOpen?: boolean;
    /** Level 2 only — the category holds exactly one sub-group (C4 §1). */
    isSingleSubGroup?: boolean;
    /** Hidden itself, or sitting under a hidden ancestor. */
    effectivelyHidden: boolean;
    searchActive: boolean;
    isRTL: boolean;
    handleProps: HandleProps;
    canMoveUp: boolean;
    canMoveDown: boolean;
    deleteMode: 'inline' | 'dialog';
    onToggleOpen?: () => void;
    onNudge: (delta: -1 | 1) => void;
    onRename: (title: string) => void;
    onToggleVisibility: () => void;
    onDelete: () => void;
    onHistory: () => void;
    onAddSubGroup?: () => void;
    onAddItem?: () => void;
    /** Level 1 only — opens CategoryStyleDialog. Absent ⇒ no palette button. */
    onEditStyle?: () => void;
}

const GroupRow = ({
    group, level, position, total, itemCount, liveCount, draftCount, isOpen, isSingleSubGroup,
    effectivelyHidden, searchActive, isRTL, handleProps, canMoveUp, canMoveDown, deleteMode,
    onToggleOpen, onNudge, onRename, onToggleVisibility, onDelete, onHistory, onAddSubGroup, onAddItem,
    onEditStyle
}: Props) => {
    const { t } = useTranslation();
    const styles = useHandleStyles();
    const [renaming, setRenaming] = useState(false);
    const [draft, setDraft] = useState(group.Title);
    const [confirming, setConfirming] = useState(false);
    const confirmTimer = useRef<any>(null);
    const committed = useRef(false);

    useEffect(() => () => clearTimeout(confirmTimer.current), []);

    const startRename = () => {
        committed.current = false;
        setDraft(group.Title);
        setRenaming(true);
    };

    const commitRename = () => {
        // blur fires after Enter as well; the guard stops a double SaveGroup.
        if (committed.current) return;
        committed.current = true;
        setRenaming(false);
        const next = draft.trim();
        if (next && next !== group.Title) onRename(next);
    };

    const cancelRename = () => {
        committed.current = true;
        setRenaming(false);
    };

    const armDelete = () => {
        if (deleteMode === 'dialog') { onDelete(); return; }
        setConfirming(true);
        clearTimeout(confirmTimer.current);
        confirmTimer.current = setTimeout(() => setConfirming(false), INLINE_CONFIRM_MS);
    };

    const isCategory = level === 1;
    // The accordion chevron points at the inline-start edge when closed — mirrored, never rotated
    // (E3 RTL_NOTES 9).
    const ClosedChevron = isRTL ? ChevronLeft : ChevronRight;

    // Category style marker. Categories ONLY — a sub-group never carries these values (§1), and
    // the check is on the level, not on the data, so a stray value could never surface here.
    const styleBg = isCategory && group.BgColor && /^#[0-9A-Fa-f]{6}$/.test(group.BgColor) ? group.BgColor : null;
    const styleIcon = isCategory ? safeIconSrc(group.IconUrl) : null;

    const meta: string[] = [];
    if (itemCount === 0) meta.push(t(`${CC}group.itemsNone`));
    else meta.push(itemCount === 1 ? t(`${CC}group.itemsOne`) : t(`${CC}group.itemsMany`, { n: itemCount }));
    if (liveCount) meta.push(t(`${CC}group.live`, { n: liveCount }));

    return (
        <Box
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: isCategory ? '10px 14px' : '8px 6px',
                background: isCategory ? 'var(--pls-d-surface, #fff)' : 'transparent',
                opacity: effectivelyHidden ? 0.62 : 1,
                flexWrap: 'wrap'
            }}
        >
            {!searchActive && (
                <>
                    <Tooltip
                        title={t(`${CC}reorder.${isCategory ? 'handleCategory' : 'handleSubGroup'}`, {
                            name: group.Title, pos: position, total
                        })}
                        PopperProps={{ style: { direction: isRTL ? 'rtl' : 'ltr' } }}
                    >
                        <button
                            type="button"
                            className={styles.handle}
                            {...handleProps}
                            aria-label={t(`${CC}reorder.${isCategory ? 'handleCategory' : 'handleSubGroup'}`, {
                                name: group.Title, pos: position, total
                            })}
                        >
                            ⋮⋮
                        </button>
                    </Tooltip>
                    {/* Disabled at the ends, NOT silently inert (§3.6). */}
                    <button
                        type="button"
                        className={styles.nudge}
                        disabled={!canMoveUp}
                        aria-label={t(`${CC}reorder.up`)}
                        onClick={() => onNudge(-1)}
                    >
                        ↑
                    </button>
                    <button
                        type="button"
                        className={styles.nudge}
                        disabled={!canMoveDown}
                        aria-label={t(`${CC}reorder.down`)}
                        onClick={() => onNudge(1)}
                    >
                        ↓
                    </button>
                </>
            )}

            {isCategory && (
                <IconButton
                    size="small"
                    aria-expanded={!!isOpen}
                    aria-label={t(`${CC}group.${isOpen ? 'collapse' : 'expand'}`)}
                    onClick={onToggleOpen}
                >
                    {isOpen ? <ExpandMore fontSize="small" /> : <ClosedChevron fontSize="small" />}
                </IconButton>
            )}

            {/* Decorative: the title beside it already names the row. An icon sits on the colour the
                portal will give it (the §6 default wash when none is set); a colour alone is a dot. */}
            {styleIcon ? (
                <Box
                    aria-hidden="true"
                    style={{
                        boxSizing: 'border-box', width: 26, height: 26, borderRadius: 7, flex: '0 0 auto',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        background: styleBg ?? CATEGORY_DEFAULT_BG, border: '1px solid rgba(38,43,58,.14)'
                    }}
                >
                    <img src={styleIcon} alt="" style={{ width: 20, height: 20, objectFit: 'contain' }} />
                </Box>
            ) : styleBg ? (
                <Box
                    aria-hidden="true"
                    style={{
                        boxSizing: 'border-box', width: 12, height: 12, borderRadius: '50%', flex: '0 0 auto',
                        background: styleBg, border: '1px solid rgba(38,43,58,.24)'
                    }}
                />
            ) : null}

            {renaming ? (
                <TextField
                    autoFocus
                    variant="outlined"
                    size="small"
                    value={draft}
                    // Groups.Title is nvarchar(255) — cap the input rather than truncate silently in T-SQL.
                    inputProps={{ 'aria-label': t(`${CC}group.renameLabel`), maxLength: 255 }}
                    onChange={e => setDraft(e.target.value)}
                    onBlur={commitRename}
                    onKeyDown={e => {
                        if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
                        // stopPropagation: Esc must cancel the rename, not close the whole screen's
                        // topmost surface.
                        else if (e.key === 'Escape') { e.stopPropagation(); cancelRename(); }
                    }}
                    style={{ maxWidth: 260, background: 'var(--pls-d-surface, #fff)' }}
                />
            ) : (
                <Typography
                    component={isCategory ? 'h3' : 'h4'}
                    style={{
                        margin: 0,
                        fontWeight: isCategory ? 700 : 600,
                        fontSize: isCategory ? 16 : 13.5,
                        color: isCategory ? 'var(--pls-d-text, #151b21)' : 'var(--pls-d-text-2, #5b6b7b)',
                        letterSpacing: isCategory ? undefined : '.02em'
                    }}
                >
                    {group.Title}
                </Typography>
            )}

            {effectivelyHidden && (
                <Typography component="span" style={{ fontSize: 12, color: 'var(--pls-d-text-2, #5A6775)', background: 'var(--pls-d-surface-2, #eef1f4)', borderRadius: 999, padding: '1px 9px' }}>
                    {t(`${CC}group.hiddenBadge`)}
                </Typography>
            )}

            <Typography component="span" style={{ fontSize: 13, color: 'var(--pls-d-text-2, #5A6775)' }}>
                {meta.join(' · ')}
                {draftCount > 0 && (
                    <Typography component="span" style={{ fontSize: 13, color: 'var(--pls-d-warning, #8A5A00)' }}>
                        {' · ' + (draftCount === 1
                            ? t(`${CC}group.draftsOne`)
                            : t(`${CC}group.drafts`, { n: draftCount }))}
                    </Typography>
                )}
            </Typography>

            {/* The portal will not print this title (C4 §1). The admin only SAYS so — it changes
                nothing about the structure. */}
            {isSingleSubGroup && (
                <Typography component="span" style={{ fontSize: 12, color: 'var(--pls-d-clal-blue, #293768)', background: 'var(--pls-d-clal-blue-tint, #EDF3FF)', borderRadius: 999, padding: '1px 9px' }}>
                    {t(`${CC}group.singleSubGroupNote`)}
                </Typography>
            )}

            <Box style={{ flex: 1 }} />

            {confirming ? (
                <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Typography component="span" style={{ fontSize: 13, color: 'var(--pls-d-danger, #c62828)', fontWeight: 700 }}>
                        {t(`${CC}deleteGroup.inlineQuestion`)}
                    </Typography>
                    <Button
                        size="small"
                        onClick={() => { setConfirming(false); onDelete(); }}
                        style={{ color: 'var(--pls-d-danger, #c62828)', fontWeight: 700, minHeight: 32 }}
                    >
                        {t(`${CC}yes`)}
                    </Button>
                    <Button size="small" onClick={() => setConfirming(false)} style={{ color: 'var(--pls-d-text-2, #5b6b7b)', minHeight: 32 }}>
                        {t(`${CC}cancel`)}
                    </Button>
                </Box>
            ) : (
                <Box style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {!isCategory && onAddItem && (
                        <Button size="small" onClick={onAddItem} style={{ color: 'var(--pls-d-brand, #FF1744)', fontWeight: 600, fontSize: 12.5 }}>
                            {t(`${CC}addItemTo`, { name: group.Title })}
                        </Button>
                    )}
                    {isCategory && onAddSubGroup && (
                        <Button size="small" onClick={onAddSubGroup} style={{ color: 'var(--pls-d-brand, #FF1744)', fontWeight: 600, fontSize: 12.5 }}>
                            {t(`${CC}addSubGroup`)}
                        </Button>
                    )}
                    {isCategory && onEditStyle && (
                        <Tooltip title={t(`${CC}group.editStyle`)} PopperProps={{ style: { direction: isRTL ? 'rtl' : 'ltr' } }}>
                            <IconButton size="small" onClick={onEditStyle} aria-label={t(`${CC}group.editStyle`)}>
                                <PaletteOutlined fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title={t(`${CC}group.rename`)} PopperProps={{ style: { direction: isRTL ? 'rtl' : 'ltr' } }}>
                        <IconButton size="small" onClick={startRename} aria-label={t(`${CC}group.rename`)}>
                            <Edit fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip
                        title={t(`${CC}group.${group.IsHidden ? 'show' : 'hide'}`)}
                        PopperProps={{ style: { direction: isRTL ? 'rtl' : 'ltr' } }}
                    >
                        <IconButton
                            size="small"
                            onClick={onToggleVisibility}
                            aria-label={t(`${CC}group.${group.IsHidden ? 'show' : 'hide'}`)}
                            aria-pressed={!!group.IsHidden}
                        >
                            {group.IsHidden ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t(`${CC}group.history`)} PopperProps={{ style: { direction: isRTL ? 'rtl' : 'ltr' } }}>
                        <IconButton size="small" onClick={onHistory} aria-label={t(`${CC}group.history`)}>
                            <History fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={t(`${CC}group.delete`)} PopperProps={{ style: { direction: isRTL ? 'rtl' : 'ltr' } }}>
                        <IconButton size="small" onClick={armDelete} aria-label={t(`${CC}group.delete`)}>
                            <DeleteOutline fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}
        </Box>
    );
};

export default GroupRow;
